import type ServiceApi from "../../api";
import {isCurrentEnvironmentInDevelopMode} from "../../environment";
import {ErrorI18n} from "../../i18n";
import {AvmApi} from "../api-constants";
import {SimpleApiInterface} from "../api-interface";
import type {ApiCredentials} from "../credentials";
import type {ApiExecutor} from "../executor";
import type {
    ValueIndicationCombinedParamError,
    ValueIndicationExternalValidationErrors,
    ValueIndicationParamError,
    ValueIndicationRequest,
    ValueIndicationResponse,
} from "../payload-types/value-indication";
import {ValidationErrorI18n} from "../validation";
import {WebError} from "../web-error";

/**
 * Error thrown when the AVM rejects a value-indication request with invalid parameters
 * (HTTP 400, error code I100). Carries the per-parameter validation errors from the API.
 */
export class IndicationValidationError extends ErrorI18n {
    readonly paramErrors: ValueIndicationParamError[];
    readonly combinedParamsErrors: ValueIndicationCombinedParamError[];

    constructor(
        errorKey: string,
        paramErrors: ValueIndicationParamError[],
        combinedParamsErrors: ValueIndicationCombinedParamError[],
    ) {
        super(errorKey);
        this.paramErrors = paramErrors;
        this.combinedParamsErrors = combinedParamsErrors;
    }

    getExternalValidationErrors(): ValueIndicationExternalValidationErrors {
        return {paramErrors: this.paramErrors, combinedParamsErrors: this.combinedParamsErrors};
    }
}

/**
 * API interface for the AVM value-indication request (POST {@link AvmApi.PATH_VALUE_INDICATION}).
 */
export class ValueIndicationApiInterface extends SimpleApiInterface<ValueIndicationResponse> {
    private readonly revaId: string | null;
    private readonly inputData: ValueIndicationRequest;
    private readonly loadDetails: boolean;
    private readonly loadComparables: string | null;
    private readonly signal?: AbortSignal;

    /** @param signal optionales AbortSignal (siehe valueIndicationThunk.ts), um eine überholte Anfrage abzubrechen */
    constructor(
        revaId: string | null,
        inputData: ValueIndicationRequest,
        loadDetails?: boolean,
        loadComparables?: boolean,
        signal?: AbortSignal,
    ) {
        super();
        this.revaId = revaId;
        this.inputData = inputData ?? {};
        this.loadDetails = !!loadDetails;
        this.loadComparables = loadComparables && loadDetails ? "BESTCOORDS" : null;
        this.signal = signal;
    }

    protected validate(): void {
        if (!this.revaId && isCurrentEnvironmentInDevelopMode()) {
            console.warn("Execution of Value Indication without a revaId!");
        }
        if (!this.inputData.segment) {
            throw new ValidationErrorI18n("segment", "indication.error.missing_segment");
        }
        if (!this.inputData.address) {
            throw new ValidationErrorI18n("address", "indication.error.missing_address");
        }
        if (this.inputData.address.length < 4) {
            throw new ValidationErrorI18n("address", "address.error.too_short");
        }
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildAvmApiUrl([AvmApi.PATH_VALUE_INDICATION], {
            revaId: this.revaId,
            details: this.loadDetails,
            comparables: this.loadComparables,
        });
    }

    protected buildPayload(): ValueIndicationRequest {
        return this.inputData;
    }

    protected async executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<ValueIndicationResponse> {
        return executor.fetchPOST(url, credentials, this.buildPayload(), this.signal);
    }

    protected handleApiErrors(e: Error): ValueIndicationResponse {
        // Error code I100: the AVM rejected the request because of invalid parameters. Surface the
        // structured per-parameter errors instead of the generic error handling.
        if (
            e instanceof WebError &&
            e.status === 400 &&
            e.getErrorCode() === "I100" &&
            Array.isArray(e.json?.paramErrors) &&
            Array.isArray(e.json?.combinedParamsErrors) &&
            (e.json.paramErrors.length > 0 || e.json.combinedParamsErrors.length > 0)
        ) {
            throw new IndicationValidationError("general.error.400", e.json.paramErrors, e.json.combinedParamsErrors);
        }
        return super.handleApiErrors(e);
    }
}
